// @flow

// used to manage a single baby, on the intake
import {isNil, find} from "lodash"

import type {
  BabyReduxState,
  BabyList,
  BabyListItem,
  ProductSetProduct,
  Baby,
  BabyState,
  Product,
  ProductSet,
  ProductOrSet,
  Survey,
  Action,
} from "../types"
import {toDinero} from "../util"

const initialState: BabyReduxState = {
  baby: null,
  list: {
    items: [],
    total: "0",
  },
  offerSending: false,
  optionals: [],
  offers: [],
  isIntakeSaving: null,
  isBabySaving: null,
  survey: null,
}

const baby = (state: BabyReduxState = initialState, action: Action) => {
  switch (action.type) {
    case "SET_BABY":
      return {
        ...state,
        baby: action.baby,
      }
    case "SET_BABY_LIST":
      return {
        ...state,
        list: action.babyList,
      }
    case "SET_OPTIONALS":
      return {
        ...state,
        optionals: action.optionals,
      }
    case "SET_OFFERS":
      return {
        ...state,
        offers: action.offers,
      }
    case "SET_OFFER_SENDING":
      return {
        ...state,
        offerSending: action.offerSending,
      }
    case "SET_SURVEY":
      const {survey: s} = action
      return {
        ...state,
        survey: [...s],
      }
    case "START_BABY_SAVING":
      return {...state, isBabySaving: true}
    case "STOP_BABY_SAVING":
      return {...state, isBabySaving: false}
    case "START_INTAKE_SAVING":
      return {...state, isIntakeSaving: true}
    case "STOP_INTAKE_SAVING":
      return {...state, isIntakeSaving: false}
    default:
      return state
  }
}

// selectors
type SurveyQuestionType = "intake_email" | "intake"
export const getSurveyQuestions = (
  survey: Survey,
  type: SurveyQuestionType
): Survey =>
  survey.filter((q) => {
    switch (type) {
      case "intake":
        return q.isIntake == true
      case "intake_email":
        return q.isIntakeEmail == true
      default:
        return true
    }
  })

// helpers

export const isProductOnBabyList = (
  product: Product | ProductSet,
  babyList: BabyList
): boolean =>
  babyList.items.filter((item) =>
    product.isSet
      ? item.productSetId == product.id
      : item.productId == product.id
  ).length > 0

export const isProductInOptionals = (
  product: Product | ProductSet,
  optionals: $ReadOnlyArray<Product>
): boolean =>
  optionals.filter((item) => item.productId == product.id).length > 0

export const getBabyListItem = (
  product: Product | ProductSet,
  babyList: BabyList,
  productSet: ?ProductSet
): BabyListItem | null => {
  return productSet && !product.isSet
    ? getBabyListItemByProductFromSet(product, babyList, productSet)
    : getBabyListItemByProduct(product, babyList)
}

export const getBabyListItemFromBabyListItem = (
  product: Product,
  babyListItem: BabyListItem
): BabyListItem | null => {
  const found = babyListItem.items.find((item) => item.productId == product.id)
  return found ? found : null
}

export const getBabyListItemByProduct = (
  product: Product | ProductSet,
  babyList: BabyList
): BabyListItem | null => {
  const found = find(babyList.items, (item) => {
    if (product.isSet) {
      return (
        item.productSetId == product.id &&
        item.productId == null &&
        !item.isInShop
      )
    }
    return item.productId == product.id && !item.isInShop && !item.isOnInvoice
  })
  return found ? found : null
}

export const getBabyListItemByProductFromSet = (
  product: Product,
  babyList: BabyList,
  productSet: ProductSet
): BabyListItem | null => {
  const found = babyList.items.find(
    (item) =>
      item.productId == product.id &&
      item.productSetId == productSet.id &&
      !item.isInShop &&
      !item.isOnInvoice
  )
  return found ? found : null
}

export const hasProductSetProductsOnBabyList = (
  productSet: ProductSet,
  babyList: BabyList
): boolean => {
  const productIds = productSet.products.map((p) => p.product.id)
  const foundBabyListItems = babyList.items.filter(
    (item) =>
      productIds.includes(item.productId) && !item.isInShop && !item.isOnInvoice
  )
  return foundBabyListItems.length > 0
}

export const hasProductSetProductsInShop = (
  productSet: ProductSet,
  babyList: BabyList
): boolean => {
  const item = find(babyList.items, (i) => i.productSetId == productSet.id)
  if (!item) {
    return false
  }
  if (item.items.length == 0) {
    return item.isInShop
  }
  const foundBabyListItemsInShop = item.items.filter((i) => i.isInShop)
  return foundBabyListItemsInShop.length > 0
}

export const isProductInShop = (
  product: ProductOrSet,
  babyList: BabyList,
  babyListItem: BabyListItem
) => {
  if (product.isSet && hasProductSetProductsInShop(product, babyList)) {
    return true
  } else {
    return babyListItem && babyListItem.isInShop == true
  }
}

type Price = {
  totalPriceSelling: any,
  totalPriceNew: any,
}

export const getBabyListTotalNew = (babyList: BabyList): any => {
  return babyList.items.reduce((acc, item) => {
    // product set
    if (item.productSetId !== null && item.productId == null) {
      const totalSubItems = item.items.reduce((subAcc, subItem): any => {
        return toDinero(subItem.priceNew).multiply(subItem.amount).add(subAcc)
      }, toDinero("0"))
      return totalSubItems.multiply(item.amount).add(acc)
    }
    // top level
    return toDinero(item.priceNew).multiply(item.amount).add(acc)
  }, toDinero("0"))
}

export const hasInvoiceableWebshopSource = (
  babyList: BabyList,
  source: string
): boolean => {
  return (
    babyList.items.filter((item) => {
      return item.webshopSource == source && item.canInvoice
    }).length > 0
  )
}

export const calcPrice = (
  item: Product | ProductSet | BabyListItem,
  itemAmount: ?number,
  babyList: BabyList
): Price => {
  const amount = itemAmount
    ? itemAmount
    : item.babyListId
      ? item.amount
      : item.typicalAmount

  // BabyListItem
  if (item.total) {
    const babyListItem: BabyListItem = (item: any)
    // Set
    if (babyListItem.productId == null) {
      const totalPriceNew = babyListItem.items
        .reduce(
          (acc, item): any =>
            toDinero(item.priceNew).multiply(item.amount).add(acc),
          toDinero("0")
        )
        .multiply(amount)
      return {
        totalPriceSelling: toDinero(babyListItem.total),
        totalPriceNew,
      }
    }

    // Product
    return {
      totalPriceSelling: toDinero(babyListItem.total),
      totalPriceNew: toDinero(babyListItem.priceNew).multiply(amount),
    }
  }

  // ProductSet
  if (item.isSet && item.products) {
    const productSet: ProductSet = (item: any)
    const mixProductBabyListItems = productSet.products
      .filter((p) => !p.product.isHidden)
      .map((p: ProductSetProduct): BabyListItem | ProductSetProduct => {
        const babyListItem = getBabyListItemByProductFromSet(
          p.product,
          babyList,
          productSet
        )
        return babyListItem ? babyListItem : p
      })

    const totalPriceNewSingle = mixProductBabyListItems.reduce(
      (acc, it): any => {
        if (it.product) {
          const psp: ProductSetProduct = (it: any)
          return toDinero(psp.product.priceNew).multiply(psp.amount).add(acc)
        }
        return toDinero(it.priceNew).multiply(it.amount).add(acc)
      },
      toDinero("0")
    )
    const totalPriceNew = totalPriceNewSingle.multiply(amount)

    const totalPriceSellingSingle = mixProductBabyListItems.reduce(
      (acc, it): any => {
        if (it.product) {
          const psp: ProductSetProduct = (it: any)
          return toDinero(psp.product.priceSelling)
            .multiply(psp.amount)
            .add(acc)
        }
        // BabyList Item
        return toDinero(it.total).add(acc)
      },
      toDinero("0")
    )
    const totalPriceSelling = totalPriceSellingSingle.multiply(amount)
    return {
      totalPriceSelling,
      totalPriceNew,
    }
  }

  const product: Product = (item: any)

  // Product
  return {
    totalPriceSelling: toDinero(product.priceSelling).multiply(amount),
    totalPriceNew: toDinero(product.priceNew).multiply(amount),
  }
}

// TODO test more!!!!!
// only productId -> product
// only productSetId -> set
// both -> product from set
export const getProductByBabyListItem = (
  babyListItem: BabyListItem,
  products: $ReadOnlyArray<ProductOrSet>
) => {
  const {productId, productSetId} = babyListItem
  if (productId && productSetId) {
    let productSet = find(products, (p) => p.isSet && p.id == productSetId)
    if (!productSet) {
      return null
    }
    let product = find(productSet.products, (p) => p.product.id == productId)
    if (product) {
      return {type: "productInSet", item: product.product, productSet}
    }
    // what if product was switched from set
    const productSets = products
      .filter((p) => p.isSet)
      .filter(
        (p) => p.products.filter((pp) => pp.product.id == productId).length > 0
      )
    productSet = productSets.length > 0 ? productSets[0] : null
    if (productSet) {
      product = find(productSet.products, (p) => p.product.id == productId)
      return {type: "productInSet", item: product.product, productSet}
    }
  }

  if (productSetId && !productId) {
    return {
      type: "productSet",
      item: find(products, (p) => p.isSet && p.id == productSetId),
    }
  }

  let item = find(products, (p) => !p.isSet && p.id == productId)
  if (item) {
    return {
      type: "product",
      item,
    }
  }

  /**
   *  Sort of edge scenario
   *
   *  Example: product was added when is_also_master and also as child.
   *  Later on, the product is changed to not is_also_master,
   *  but exists as child on baby list.
   */
  const productsInProductSets = products
    .filter((p) => p.isSet)
    .map((p) => p.products)
    .flat()
  item = find(productsInProductSets, (p) => p.product.id == productId)
  if (item) {
    return {
      type: "product",
      item: {...item.product, productSetId: null},
    }
  } else {
    console.warn("EDGE CASE, fundamental", item)
    return null
  }
}

// TODO TEST
export const getBabyListItemsByCategory = (
  babyList: BabyList,
  products: $ReadOnlyArray<Product | ProductSet>,
  categories: any
): any => {
  const enrichedWithCategory = babyList.items.map((item): any => {
    const product = getProductByBabyListItem(item, products)
    if (product && product.item) {
      return {...item, category: product.item.categoryId}
    }
    console.warn("EDGE CASE FOR ", item)
    // edge case
    return item
  })
  const categoryIds = categories.map((c) => c.id)
  const sortedEnrichedWithCategory = enrichedWithCategory.sort(
    ({category: categoryA}, {category: categoryB}) => {
      if (categoryIds.indexOf(categoryA) > categoryIds.indexOf(categoryB)) {
        return -1
      }
      if (categoryIds.indexOf(categoryA) < categoryIds.indexOf(categoryB)) {
        return 1
      }
      return 0
    }
  )
  let sorted = categories.map((c) => ({category: c, items: []}))
  sortedEnrichedWithCategory.forEach((item) => {
    sorted = sorted.map((c) => {
      if (c.category.id === item.category) {
        return {
          ...c,
          items: [...c.items, item],
        }
      }
      return {...c}
    })
  })
  return sorted.filter((c) => c.items.length > 0)
}

export const hasState = (baby: Baby, state: BabyState) =>
  baby.states && baby.states.includes(state)

export const hasRemarks = (babyList: BabyList): boolean =>
  babyList.items.filter(({remarks}) => !isNil(remarks) && remarks != "")
    .length > 0

export default baby
